import nsApi from '@netsapiens/netsapiens-js/dist/api';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import {
  Avatar,
  Drawer,
  Fab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import DashboardIcon from '@material-ui/icons/ViewQuilt';
import GroupIcon from '@material-ui/icons/Group';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import Lock from '@material-ui/icons/Lock';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SettingsIcon from '@material-ui/icons/SettingsSharp';
import Star from '@material-ui/icons/Star';
import pack from '../../../../package.json';

import styles from './styles.module.css';
import * as actions from '../../../state';
import { createBoard, changeBoard } from '../../../events';
import {
  checkUserPermission as can, // can(user,'action')
  parseUrl,
  getInitials,
} from '../../../utils';

const messages = defineMessages({
  DELETE: {
    id: 'DELETE',
    defaultMessage: 'Delete',
  },
  LOGOUT: {
    id: 'LOGOUT',
    defaultMessage: 'Log Out',
  },
  SETTINGS: {
    id: 'SETTINGS',
    defaultMessage: 'Settings',
  },
  NEW_BOARD: {
    id: 'NEW_BOARD',
    defaultMessage: 'New Board',
  },
  FAVORITE_BOARDS: {
    id: 'FAVORITE_BOARDS',
    defaultMessage: 'Favorite Boards',
  },
  MY_BOARDS: {
    id: 'MY_BOARDS',
    defaultMessage: 'My Boards',
  },
  SHARED_BOARDS: {
    id: 'SHARED_BOARDS',
    defaultMessage: 'Shared with me',
  },
});

function LeftSidebar({ intl }) {
  const dispatch = useDispatch();

  const appURL = useSelector((state) => actions.selectConfig(state, 'appURL'));
  const apiHash = useSelector((state) => actions.selectConfig(state, 'apiHash'));
  const user = useSelector(actions.readStateUser);
  const boardList = useSelector(actions.readStateBoardList);
  const layout = useSelector(actions.selectLayout);

  const [isExpanded, setIsExpanded] = useState(
    (localStorage.getItem('isExpanded')
    && JSON.parse(localStorage.getItem('isExpanded')))
    || {},
  );

  const toggleDrawer = () => {
    dispatch(actions.showLeftSidebar(false));
  };

  const toggleSection = (section) => () => {
    isExpanded[section] = !isExpanded[section];
    setIsExpanded({ ...isExpanded });
    localStorage.setItem('isExpanded', JSON.stringify(isExpanded));
  };

  const createNewBoard = () => {
    dispatch(actions.isLoading(true));
    dispatch(actions.showLeftSidebar(false));

    dispatch(createBoard(intl, messages));

    const newPath = `${appURL}new`;
    window.history.pushState({ urlPath: newPath }, '', newPath);
  };

  const routeToBoard = (boardId) => {
    layout.showLeftSidebar = false;
    dispatch(changeBoard(boardId));
  };

  const flushCache = () => {
    const keys = Object.keys(localStorage);
    let i = keys.length;
    // eslint-disable-next-line no-plusplus
    while (i--) {
      if (keys[i].startsWith('lscache')) localStorage.removeItem(keys[i]);
    }
  };

  const logout = () => {
    nsApi.logout(apiHash).then(() => {
      flushCache();
      window.location.reload();
    });
  };

  const renderUserInfo = () => {
    const avatarContent = user.gravatar ? (
      <Avatar
        className={`${styles.avatar} ${styles.userAvatar}`}
        src={user.gravatar}
      />
    ) : (
      <Avatar className={`${styles.avatar} ${styles.userAvatar}`}>
        <span>
          {getInitials(user.displayName)}
        </span>
      </Avatar>
    );

    return (
      <ListItem>
        <ListItemAvatar>
          {avatarContent}
        </ListItemAvatar>

        <ListItemText
          primary={user.displayName}
          secondary={user.userId}
          className={styles.displayName}
        />
      </ListItem>
    );
  };

  const renderBoardLinks = (boards) => {
    const routeParams = parseUrl(window.location);
    const activeBoardId = routeParams.boardId;

    return boards.map((board) => (
      <ListItem
        button
        onClick={() => routeToBoard(board.id)}
        key={board.id}
        className={`${styles.rightLinks} ${activeBoardId === board.id ? styles.activeLink : ''}`}
        style={{
          borderLeft: activeBoardId === board.id ? `3px solid ${layout.brandPrimaryColor}` : '',
        }}
      >
        <ListItemText primary={board.name} className={styles.listText} title={board.name} />
      </ListItem>
    ));
  };

  const renderBoardListPanel = (bl, listGroup) => {
    let groupIcon;
    let groupName;
    let emptyMsg;

    switch (listGroup) {
      case 'favoriteBoards':
        groupIcon = <Star className={styles.sectionIcon} />;
        groupName = <FormattedMessage {...messages.FAVORITE_BOARDS} />;
        break;
      case 'myBoards':
        groupIcon = <DashboardIcon className={styles.sectionIcon} />;
        groupName = <FormattedMessage {...messages.MY_BOARDS} />;
        break;
      case 'sharedBoards':
        groupIcon = <GroupIcon className={styles.sectionIcon} />;
        groupName = <FormattedMessage {...messages.SHARED_BOARDS} />;
        break;
      default:
    }

    return (
      <Accordion
        expanded={isExpanded[listGroup] || false}
        onChange={toggleSection(listGroup)}
        className={styles.accordion}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className={styles.accordionSummary}
          classes={{
            content: styles.expansionTitle,
            expandIcon: styles.expansionIcon,
          }}
        >
          {groupIcon}
          {' '}
          {groupName}
        </AccordionSummary>

        <AccordionDetails classes={{ root: styles.expansionDetails }}>
          {(bl.length && renderBoardLinks(bl)) || (
            <Typography variant="caption" gutterBottom align="center">
              {emptyMsg}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const favoriteBoards = boardList?.filter((board) => board.favorite) || [];
  const myBoards = boardList?.filter((board) => String(board.owner_id) === String(user.userId)) || [];
  const sharedBoards = boardList?.filter((board) => String(board.owner_id) !== String(user.userId)) || [];

  return (
    <Drawer
      open={layout.showLeftSidebar}
      classes={{
        paper: styles.sideBar,
        modal: styles.leftSheetsModal,
      }}
      ModalProps={{ onClose: toggleDrawer }}
    >
      <List className={styles.topAnchoredContent}>
        {renderUserInfo()}

        {can(user, 'CREATE_BOARDS') && (
          <ListItem>
            <Fab
              aria-label={<FormattedMessage {...messages.DELETE} />}
              className={styles.createNewBoardBtn}
              onClick={createNewBoard}
            >
              <Add className={styles.newBoardIcon} />
              <FormattedMessage {...messages.NEW_BOARD} />
            </Fab>
          </ListItem>
        )}
      </List>

      <List className={styles.boardList}>
        {favoriteBoards.length ? renderBoardListPanel(favoriteBoards, 'favoriteBoards') : ''}

        {myBoards.length ? renderBoardListPanel(myBoards, 'myBoards') : ''}

        {sharedBoards.length ? renderBoardListPanel(sharedBoards, 'sharedBoards') : ''}

        {false && ( // disabled in v40
          <ListItem button style={{ borderTop: '1px solid #e4e4e4' }}>
            <ListItemIcon className={styles.otherActions}>
              <SettingsIcon />
            </ListItemIcon>

            <ListItemText primary={<FormattedMessage {...messages.SETTINGS} />} />
          </ListItem>
        )}

        <ListItem className={styles.versionNumber} style={{ borderTop: '1px solid #e4e4e4' }}>
          <ListItemIcon className={styles.otherActions}>
            <InfoOutlined />
          </ListItemIcon>

          <ListItemText id="version-number" primary={pack.version} />
        </ListItem>

        <ListItem button onClick={logout}>
          <ListItemIcon className={styles.otherActions}>
            <Lock />
          </ListItemIcon>

          <ListItemText primary={<FormattedMessage {...messages.LOGOUT} />} />
        </ListItem>
      </List>
    </Drawer>
  );
}

export default injectIntl(LeftSidebar);
